import { createGlobalStyle } from "styled-components";

import {
  SPACING,
  COLORS_GLOBAL,
  COLORS_THEME_DARK,
  COLORS_THEME_WHITE,
  WEIGHTS,
  FONT_SIZES,
  SHADOWS,
  GRADIENTS,
  OVERLAYS,
  ANIMATION,
} from "../../utils/constants";

const GlobalStyles = createGlobalStyle`
  /* 
    1. Use a more-intuitive box-sizing model.
  */
  *, *::before, *::after {
    box-sizing: border-box;
  }

  /* 
    2. Remove default margin
  */
  * {
    margin: 0;
  }

  /*
    3. Allow percentage-based heights in the application
  */
  html, body, #root {
    height: 100%;
  }

  /*
    Typographic tweaks!
    4. Add accessible line-height
    5. Improve text rendering
  */
  body {
    line-height: 1.6;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    font-family: 'Crimson Pro', sans-serif;
    overflow-y: auto !important;
  }

  /* Typography optimization for readability */
  p, li, td, span {
    line-height: 1.7;
  }

  /* Heading hierarchy with optimal contrast */
  h1 {
    font-size: var(--text-display-lg);
    font-weight: ${WEIGHTS.bold};
    line-height: 1.2;
    letter-spacing: -0.02em;
    margin-bottom: ${SPACING.md}px;
  }

  h2 {
    font-size: var(--text-display);
    font-weight: ${WEIGHTS.bold};
    line-height: 1.3;
    letter-spacing: -0.015em;
    margin-bottom: ${SPACING.sm + SPACING.xs + SPACING.xxs}px;
  }

  h3 {
    font-size: var(--text-xxl);
    font-weight: 600;
    line-height: 1.4;
    letter-spacing: -0.01em;
    margin-bottom: ${SPACING.sm + SPACING.xs}px;
  }

  h4 {
    font-size: var(--text-xl);
    font-weight: 600;
    line-height: 1.5;
    letter-spacing: 0;
    margin-bottom: ${SPACING.sm + SPACING.xxs}px;
  }

  h5 {
    font-size: var(--text-lg);
    font-weight: ${WEIGHTS.medium};
    line-height: 1.5;
    letter-spacing: 0;
    margin-bottom: ${SPACING.sm}px;
  }

  h6 {
    font-size: var(--text-md);
    font-weight: ${WEIGHTS.medium};
    line-height: 1.6;
    letter-spacing: 0.01em;
    margin-bottom: ${SPACING.sm}px;
  }

  /* Small text optimization for better legibility */
  small, .small-text {
    font-size: var(--text-base);
    letter-spacing: 0.02em;
    line-height: 1.6;
  }

  /* Extra small text (labels, captions) */
  .caption, caption {
    font-size: var(--text-xs);
    letter-spacing: 0.025em;
    line-height: 1.5;
  }

  /* Dark theme specific typography optimizations */
  [data-theme="dark"] {
    body {
      line-height: 1.75; /* Increased for better dark mode readability */
    }

    p, li, td, span {
      line-height: 1.85; /* Enhanced spacing for dark backgrounds */
    }

    /* Enhanced letter-spacing for small text in dark mode */
    small, .small-text {
      font-size: var(--text-base);
      letter-spacing: 0.035em; /* Increased for better legibility */
      line-height: 1.65;
    }

    .caption, caption {
      font-size: var(--text-xs);
      letter-spacing: 0.04em; /* Increased for better tracking */
      line-height: 1.6;
    }

    /* Improve contrast for headings in dark mode */
    h1 {
      font-weight: ${WEIGHTS.bold};
      letter-spacing: -0.025em;
    }

    h2 {
      font-weight: ${WEIGHTS.bold};
      letter-spacing: -0.02em;
    }

    h3 {
      font-weight: 650; /* Slightly heavier for better contrast */
      letter-spacing: -0.015em;
    }

    h4 {
      font-weight: 625;
    }

    /* Better readability for labels and inputs */
    label, input, button, select, textarea {
      letter-spacing: 0.015em;
    }
  }

  /*
    6. Improve media defaults
  */
  img, picture, video, canvas, svg {
    display: block;
    max-width: 100%;
  }

  /*
    7. Remove built-in form typography styles
  */
  input, button, textarea, select {
    font: inherit;
  }

  /*
    8. Avoid text overflows
  */
  p, h1, h2, h3, h4, h5, h6 {
    overflow-wrap: break-word;
  }

  /*
    9. Create a root stacking context
  */
  #root, #__next {
    isolation: isolate;
  }
  
  /*
    10. Smooth theme transitions
    Respects user preference for reduced motion
  */
  @media (prefers-reduced-motion: no-preference) {
    :root {
      transition: 
        background-color ${ANIMATION.duration.moderate} ${ANIMATION.easing.standard},
        color ${ANIMATION.duration.moderate} ${ANIMATION.easing.standard},
        border-color ${ANIMATION.duration.moderate} ${ANIMATION.easing.standard};
    }
    
    * {
      transition: 
        background-color ${ANIMATION.duration.moderate} ${ANIMATION.easing.standard},
        color ${ANIMATION.duration.moderate} ${ANIMATION.easing.standard},
        border-color ${ANIMATION.duration.moderate} ${ANIMATION.easing.standard},
        box-shadow ${ANIMATION.duration.normal} ${ANIMATION.easing.standard};
    }
    
    /* Exclude skeleton animations from theme transitions */
    [class*="Skeleton"],
    [class*="skeleton"],
    [class*="Loading"] {
      transition: none !important;
    }
  }

  /*
    11. Performance optimizations
    Use CSS containment for better rendering performance
  */
  
  /* Contain cards and components to prevent layout thrashing.
     Exclude any subtree wrapping an MUI chart: @mui/x-charts v9 portals its
     tooltip into the chart container, so CSS containment here would establish
     a containing block for the position:fixed tooltip (offsetting it) and the
     paint containment would clip it. */
  [class*="Card"]:not(.MuiChartsSurface-root):not(:has(.MuiChartsSurface-root)),
  [class*="card"]:not(.MuiChartsSurface-root):not(:has(.MuiChartsSurface-root)),
  [class*="Panel"]:not(.MuiChartsSurface-root):not(:has(.MuiChartsSurface-root)),
  [class*="panel"]:not(.MuiChartsSurface-root):not(:has(.MuiChartsSurface-root)) {
    contain: layout style paint;
  }

  /* Contain modal and dropdown overlays */
  [class*="Modal"],
  [class*="modal"],
  [class*="Dropdown"],
  [class*="dropdown"],
  [role="dialog"],
  [role="menu"] {
    contain: layout style;
  }

  /* Theme toggle optimization - prevent layout shifts */
  [data-theme] {
    contain: layout style paint;
  }

  /* Optimize chart rendering. Exclude wrappers that contain an MUI chart
     surface (incl. MUI's own MuiChartsWrapper/MuiChartContainer divs, which
     match [class*="Chart"]) so the v9 in-container tooltip is not offset by a
     containing block. */
  [class*="Chart"]:not(.MuiChartsSurface-root):not(:has(.MuiChartsSurface-root)),
  [class*="chart"]:not(.MuiChartsSurface-root):not(:has(.MuiChartsSurface-root)),
  svg {
    contain: layout style;
  }

  /* Use GPU acceleration for animations */
  [class*="Skeleton"],
  [class*="skeleton"],
  [class*="Loading"],
  [class*="Animate"],
  [class*="animate"] {
    transform: translateZ(0);
    will-change: transform;
  }

  input:-webkit-autofill,
  input:-webkit-autofill:hover,
  input:-webkit-autofill:focus,
  input:-webkit-autofill:active {
    transition: background-color 9999s ease-in-out 0s;
  }

  /* Remove Arrows/Spinners  Chrome, Safari, Edge, Opera */
  input::-webkit-outer-spin-button,
  input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  /*Remove Arrows/Spinners  Firefox */
  input[type=number] {
    -moz-appearance: textfield;
  }

  /* GLOBAL COLORS */
  html {
    /* Gray Scale */
    --color-gray-100: ${COLORS_GLOBAL.gray100};
    --color-gray-200: ${COLORS_GLOBAL.gray200};
    --color-gray-300: ${COLORS_GLOBAL.gray300};
    --color-gray-500: ${COLORS_GLOBAL.gray500};
    --color-gray-600: ${COLORS_GLOBAL.gray600};
    --color-gray-700: ${COLORS_GLOBAL.gray700};
    --color-gray-800: ${COLORS_GLOBAL.gray800};
    --color-gray-900: ${COLORS_GLOBAL.gray900};
    --color-gray-border: ${COLORS_GLOBAL.grayBorder};
    --color-gray-background: ${COLORS_GLOBAL.grayBackground};
    --color-gray-pill: ${COLORS_GLOBAL.grayPill};

    /* Brand Colors */
    --color-primary-color-background: ${COLORS_THEME_DARK.primaryColorBackground};
    --color-blue: ${COLORS_GLOBAL.blue};
    --color-blue-2: ${COLORS_GLOBAL.blue2};
    --color-blue-3: ${COLORS_GLOBAL.blue3};
    --color-blue-4: ${COLORS_GLOBAL.blue4};
    --color-yellow-4: ${COLORS_GLOBAL.yellow4};
    --color-yellow-5: ${COLORS_GLOBAL.yellow5};
    --color-yellow-6: ${COLORS_GLOBAL.yellow6};
    --color-purple: ${COLORS_GLOBAL.purple};

    /* UI Colors */
    --color-light-gray-6: ${COLORS_GLOBAL.lightGray6};
    --color-light-gray-9: ${COLORS_GLOBAL.lightGray9};
    --color-medium-gray: ${COLORS_GLOBAL.mediumGray};
    --color-dark-gray: ${COLORS_GLOBAL.darkGray};

    --color-border: ${COLORS_GLOBAL.border};
    --color-black: ${COLORS_GLOBAL.black};
    --color-white: ${COLORS_GLOBAL.white};

    /* Status Colors */
    --color-success: ${COLORS_GLOBAL.success};
    /* AA-compliant success *foreground* for small text (>=4.5:1 on the surface). --color-success
       is a brighter accent (#059669 in the light theme) that fails AA at body sizes, so data text
       such as signed report figures must use this darker token instead. */
    --color-success-text: ${COLORS_GLOBAL.success};
    --color-success-6: ${COLORS_GLOBAL.success6};
    --color-success-hover: ${COLORS_GLOBAL.successHover};
    --color-success-background: ${COLORS_GLOBAL.successBackground};
    --color-success-background-2: ${COLORS_GLOBAL.successBackground2};
    --color-success-border: ${COLORS_GLOBAL.successBorder};

    --color-info: ${COLORS_GLOBAL.info};
    --color-info-background: ${COLORS_GLOBAL.infoBackground};
    --color-info-border: ${COLORS_GLOBAL.infoBorder};

    --color-warning: ${COLORS_GLOBAL.warning};
    --color-warning-2: ${COLORS_GLOBAL.warning2};
    --color-warning-4: ${COLORS_GLOBAL.warning4};
    --color-warning-background: ${COLORS_GLOBAL.warningBackground};
    --color-warning-background-2: ${COLORS_GLOBAL.warningBackground2};
    --color-warning-border: ${COLORS_GLOBAL.warningBorder};

    --color-error: ${COLORS_GLOBAL.error};
    --color-error-2: ${COLORS_GLOBAL.error2};
    --color-error-5: ${COLORS_GLOBAL.error5};
    --color-error-hover: ${COLORS_GLOBAL.errorHover};
    --color-error-background: ${COLORS_GLOBAL.errorBackground};
    --color-error-background-2: ${COLORS_GLOBAL.errorBackground2};
    --color-error-border: ${COLORS_GLOBAL.errorBorder};
    --color-error-border-2: ${COLORS_GLOBAL.errorBorder2};

    /* Font Weights */
    --font-weight-normal: ${WEIGHTS.normal};
    --font-weight-medium: ${WEIGHTS.medium};
    --font-weight-bold: ${WEIGHTS.bold};

    /* Font Sizes */
    --text-xs: ${FONT_SIZES.xs};
    --text-sm: ${FONT_SIZES.sm};
    --text-base: ${FONT_SIZES.base};
    --text-md: ${FONT_SIZES.md};
    --text-lg: ${FONT_SIZES.lg};
    --text-xl: ${FONT_SIZES.xl};
    --text-xxl: ${FONT_SIZES.xxl};
    --text-xxxl: ${FONT_SIZES.xxxl};
    --text-display: ${FONT_SIZES.display};
    --text-display-lg: ${FONT_SIZES.displayLg};
    --text-display-xl: ${FONT_SIZES.displayXl};

    /* Shadows */
    /* The Flat-by-Default Rule: a surface at rest carries no shadow in dark mode, only in light. */
    --shadow-card-rest: none;
    --shadow-small: ${SHADOWS.small};
    --shadow-small-2: ${SHADOWS.small2};
    --shadow-medium: ${SHADOWS.medium};
    --shadow-medium-2: ${SHADOWS.medium2};
    --shadow-medium-3: ${SHADOWS.medium3};
    --shadow-large: ${SHADOWS.large};
    --shadow-large-2: ${SHADOWS.large2};
    --shadow-large-3: ${SHADOWS.large3};
    --shadow-xlarge: ${SHADOWS.xlarge};
    --shadow-focus: ${COLORS_THEME_DARK.focusShadow};
    --shadow-dropdown: ${SHADOWS.dropdown};
    --shadow-button: ${COLORS_THEME_DARK.shadowButton};
    --shadow-hover: ${SHADOWS.hover};
    --shadow-active: ${SHADOWS.active};

    /* Empty State Colors */
    --empty-state-background: ${COLORS_THEME_DARK.emptyStateBackground};
    --empty-state-icon-background: ${COLORS_THEME_DARK.emptyStateIconBackground};
    --empty-state-icon-color: ${COLORS_THEME_DARK.emptyStateIconColor};
    --empty-state-border: ${COLORS_THEME_DARK.emptyStateBorder};
    
    /* Overlay Background */
    --color-background-overlay: ${COLORS_THEME_DARK.backgroundOverlay};

    /* Gradients */
    --gradient-skeleton: ${COLORS_THEME_DARK.skeletonGradient};
    --gradient-skeleton-2: ${COLORS_THEME_DARK.skeletonGradient2};
    --gradient-primary: ${COLORS_THEME_DARK.gradientPrimary};
    --gradient-card: ${GRADIENTS.card};
    --gradient-warning: ${GRADIENTS.warning};
    --gradient-error: ${GRADIENTS.error};

    /* Overlays */
    --overlay-white-10: ${OVERLAYS.white10};

    /* Dark Theme Default Colors */
    --color-background-0: ${COLORS_THEME_DARK.backgroundBody};
    --color-background-2: ${COLORS_THEME_DARK.backgroundCard};
    --color-background-3: ${COLORS_THEME_DARK.backgroundShimmer};
    --color-background-disabled: ${COLORS_THEME_DARK.backgroundBodyDisabled};

    --color-table-header-background: ${COLORS_THEME_DARK.table.headerBackground};
    --color-table-header-color: ${COLORS_THEME_DARK.table.headerColor};
    --color-table-header-hover: ${COLORS_THEME_DARK.table.headerHover};
    --color-table-header-active: ${COLORS_THEME_DARK.table.headerActive};
    --color-golden: ${COLORS_THEME_DARK.table.headerColor};

    --color-card-background: ${COLORS_THEME_DARK.card.background};
    --color-card-background-transparent: ${COLORS_THEME_DARK.card.backgroundTransparent};
    --color-card-background-deactivated: ${GRADIENTS.deactivatedDark};
    --color-card-border: ${COLORS_THEME_DARK.card.border};
    --color-card-metric-background: ${COLORS_THEME_DARK.card.metricBackground};
    --color-card-metric-progress-background: ${COLORS_GLOBAL.white};
    --color-card-metric-border: ${COLORS_THEME_DARK.card.metricBorder};

    --color-nav-background: ${COLORS_THEME_DARK.navbar.background};
    --color-nav-header-color: ${COLORS_THEME_DARK.navbar.headerColor};
    --color-nav-icon-background: ${COLORS_THEME_DARK.navbar.iconBackground};
    --color-nav-item-hover: ${OVERLAYS.white10};
    --color-nav-item-hover-active: ${OVERLAYS.white20};
    --color-nav-item-text: ${OVERLAYS.white90};
    --color-nav-border-focus: ${OVERLAYS.white60};
    --color-nav-divider: ${OVERLAYS.white10};
    --color-nav-active-accent: ${COLORS_THEME_DARK.primary};
    --color-nav-active-underline: ${COLORS_THEME_DARK.primary};
    --color-nav-bottom-accent: ${COLORS_THEME_DARK.primary};
    --color-nav-bottom-accent-bg: ${COLORS_THEME_DARK.payment.dateChipBg};

    --color-payment-date-chip-background: ${COLORS_THEME_DARK.payment.dateChipBg};
    --color-payment-status-complete-background: ${COLORS_THEME_DARK.payment.statusCompletedBg};
    --color-payment-status-complete-color: ${COLORS_THEME_DARK.payment.statusCompletedColor};
    --color-payment-status-pending-background: ${COLORS_THEME_DARK.payment.statusPendingBg};
    --color-payment-status-pending-color: ${COLORS_THEME_DARK.payment.statusPendingColor};
    --color-payment-status-failed-background: ${COLORS_THEME_DARK.payment.statusFailedBg};
    --color-payment-status-failed-color: ${COLORS_THEME_DARK.payment.statusFailedColor};

    --color-calendar-hover-background: ${COLORS_THEME_DARK.calendar.hoverBackground};
    --color-calendar-disabled-background: ${COLORS_THEME_DARK.calendar.disabledBackground};

    --color-tab-background: ${COLORS_THEME_DARK.tab.background};

    --color-primary: ${COLORS_THEME_DARK.primary};
    --color-on-primary: ${COLORS_THEME_DARK.backgroundBody};
    --color-primary-hover: ${COLORS_THEME_DARK.primaryHover};

    --color-secondary: ${COLORS_THEME_DARK.secondary};
    --color-secondary-hover: ${COLORS_THEME_DARK.secondaryHover};

    --color-ternary: ${COLORS_THEME_DARK.ternary};

    --color-button-cancel-color: ${COLORS_THEME_DARK.button.cancelColor};
    --color-button-cancel-background: ${COLORS_THEME_DARK.button.cancelBackground};
    --color-button-cancel-border: ${COLORS_THEME_DARK.button.cancelBorder};
    --color-button-cancel-hover-background: ${COLORS_THEME_DARK.button.cancelHoverBackground};
    --color-button-cancel-hover-border: ${COLORS_THEME_DARK.button.cancelHoverBorder};
    --color-button-cancel-active-background: ${COLORS_THEME_DARK.button.cancelActiveBackground};
    --color-button-cancel-active-border: ${COLORS_THEME_DARK.button.cancelActiveBorder};

    --color-box-shadow: ${COLORS_THEME_DARK.hover};

    --color-text-default: ${COLORS_THEME_DARK.textPrimary};
    --color-text-secondary: ${COLORS_THEME_DARK.textSecondary};
    --color-text-muted: ${COLORS_THEME_DARK.textMuted};

    --color-negative: ${COLORS_THEME_DARK.negativeColor};
    --color-negative-border: ${COLORS_THEME_DARK.negativeColorBorder};

    --color-positive: ${COLORS_THEME_DARK.positiveColor};
    --color-positive-border: ${COLORS_THEME_DARK.positiveColorBorder};

    /* Dark theme overrides for better contrast */
    --color-success: ${COLORS_THEME_DARK.statusSuccess};
    /* On dark surfaces the bright status green already clears AA, so success text keeps it. */
    --color-success-text: ${COLORS_THEME_DARK.statusSuccess};
    --color-success-hover: ${COLORS_THEME_DARK.statusSuccessHover};
    --color-success-background: ${COLORS_THEME_DARK.statusSuccessBackground};
    --color-success-border: ${COLORS_THEME_DARK.statusSuccessBorder};
    --color-status-success: ${COLORS_THEME_DARK.statusSuccess};
    --color-status-success-background: ${COLORS_THEME_DARK.statusSuccessBackground};
    --color-status-success-background-soft: ${COLORS_THEME_DARK.statusSuccessBackgroundSoft};
    --color-status-success-border: ${COLORS_THEME_DARK.statusSuccessBorder};
    --color-status-success-border-soft: ${COLORS_THEME_DARK.statusSuccessBorderSoft};

    --color-error: ${COLORS_THEME_DARK.statusError};
    --color-error-hover: ${COLORS_THEME_DARK.statusErrorHover};
    --color-error-background: ${COLORS_THEME_DARK.statusErrorBackground};
    --color-error-border: ${COLORS_THEME_DARK.statusErrorBorder};
    --color-status-error: ${COLORS_THEME_DARK.statusError};
    --color-status-error-background: ${COLORS_THEME_DARK.statusErrorBackground};
    --color-status-error-background-soft: ${COLORS_THEME_DARK.statusErrorBackgroundSoft};
    --color-status-error-border: ${COLORS_THEME_DARK.statusErrorBorder};
    --color-status-error-border-soft: ${COLORS_THEME_DARK.statusErrorBorderSoft};

    --color-warning: ${COLORS_THEME_DARK.statusWarning};
    --color-warning-hover: ${COLORS_THEME_DARK.statusWarningHover};
    --color-warning-background: ${COLORS_THEME_DARK.statusWarningBackground};
    --color-warning-border: ${COLORS_THEME_DARK.statusWarningBorder};
    --color-status-warning: ${COLORS_THEME_DARK.statusWarning};
    --color-status-warning-background: ${COLORS_THEME_DARK.statusWarningBackground};
    --color-status-warning-background-soft: ${COLORS_THEME_DARK.statusWarningBackgroundSoft};
    --color-status-warning-border: ${COLORS_THEME_DARK.statusWarningBorder};
    --color-status-warning-border-soft: ${COLORS_THEME_DARK.statusWarningBorderSoft};

    --color-info: ${COLORS_THEME_DARK.statusInfo};
    --color-info-background: ${COLORS_THEME_DARK.statusInfoBackground};
    --color-info-border: ${COLORS_THEME_DARK.statusInfoBorder};
    --color-status-info: ${COLORS_THEME_DARK.statusInfo};
    --color-status-info-background: ${COLORS_THEME_DARK.statusInfoBackground};
    --color-status-info-background-soft: ${COLORS_THEME_DARK.statusInfoBackgroundSoft};
    --color-status-info-border: ${COLORS_THEME_DARK.statusInfoBorder};
    --color-status-info-border-soft: ${COLORS_THEME_DARK.statusInfoBorderSoft};

    --color-select-text: ${COLORS_THEME_DARK.textPrimary};
    --color-select-option-text: ${COLORS_THEME_DARK.textPrimary};

    --color-select-icon: ${COLORS_THEME_DARK.textSecondary};

    /* Hero section and detail page colors */
    --hero-background: ${COLORS_THEME_DARK.heroBackground};
    --hero-footer-background: ${COLORS_THEME_DARK.heroFooterBackground};
    --detail-icon-background: ${COLORS_THEME_DARK.detailIconBackground};
    --hover-background: ${COLORS_THEME_DARK.hoverBackground};
    
    /* Tooltip colors */
    --color-tooltip-bg: ${COLORS_THEME_DARK.tooltipBackground};
    --color-tooltip-text: ${COLORS_THEME_DARK.tooltipText};
    --color-tooltip-border: ${COLORS_THEME_DARK.tooltipBorder};
    
    /* Border colors */
    --color-border-donut: ${COLORS_THEME_DARK.textDisabled};
    --color-border: ${COLORS_THEME_DARK.border};
    --color-border-light: ${COLORS_THEME_DARK.borderLight};
    
    /* Tab colors */
    --color-tab-hover-background: ${COLORS_THEME_DARK.tabHoverBackground};
    
    /* Info box colors */
    --color-info-box-background: ${COLORS_THEME_DARK.infoBoxBackground};
    --color-info-box-border: ${COLORS_THEME_DARK.infoBoxBorder};
    --color-info-box-text: ${COLORS_THEME_DARK.infoBoxText};
    
    /* Toggle button colors */
    --color-toggle-group-background: ${COLORS_THEME_DARK.toggleGroupBackground};
    --color-toggle-active-background: ${COLORS_THEME_DARK.toggleActiveBackground};
    --color-toggle-hover-background: ${COLORS_THEME_DARK.toggleHoverBackground};
    --shadow-toggle-active: ${COLORS_THEME_DARK.toggleActiveShadow};
    
    /* Skeleton loader gradients for dark theme */
    --gradient-skeleton: ${COLORS_THEME_DARK.skeletonGradient};
    --gradient-skeleton-2: ${COLORS_THEME_DARK.skeletonGradient2};

    /* Focus shadows for dark theme */
    --shadow-focus: ${COLORS_THEME_DARK.focusShadow};

    /* Notification Colors - Dark mode */
    --notification-success-text: ${COLORS_THEME_DARK.notificationSuccessText};
    --notification-success-border: ${COLORS_THEME_DARK.notificationSuccessBorder};
    --notification-success-background-start: ${COLORS_THEME_DARK.notificationSuccessBackgroundStart};
    --notification-success-background-end: ${COLORS_THEME_DARK.notificationSuccessBackgroundEnd};

    --notification-info-text: ${COLORS_THEME_DARK.notificationInfoText};
    --notification-info-border: ${COLORS_THEME_DARK.notificationInfoBorder};
    --notification-info-background-start: ${COLORS_THEME_DARK.notificationInfoBackgroundStart};
    --notification-info-background-end: ${COLORS_THEME_DARK.notificationInfoBackgroundEnd};

    --notification-warning-text: ${COLORS_THEME_DARK.notificationWarningText};
    --notification-warning-border: ${COLORS_THEME_DARK.notificationWarningBorder};
    --notification-warning-background-start: ${COLORS_THEME_DARK.notificationWarningBackgroundStart};
    --notification-warning-background-end: ${COLORS_THEME_DARK.notificationWarningBackgroundEnd};

    --notification-error-text: ${COLORS_THEME_DARK.notificationErrorText};
    --notification-error-border: ${COLORS_THEME_DARK.notificationErrorBorder};
    --notification-error-background-start: ${COLORS_THEME_DARK.notificationErrorBackgroundStart};
    --notification-error-background-end: ${COLORS_THEME_DARK.notificationErrorBackgroundEnd};
}

[data-theme="light"]{
    --shadow-card-rest: ${SHADOWS.small};
    --color-background-0: ${COLORS_THEME_WHITE.backgroundBody};
    --color-background-2: ${COLORS_THEME_WHITE.backgroundCard};
    --color-background-3: ${COLORS_THEME_WHITE.backgroundShimmer};
    --color-background-disabled: ${COLORS_THEME_WHITE.backgroundBodyDisabled};

    --color-primary-color-background: ${COLORS_THEME_WHITE.primaryColorBackground};
    --gradient-primary: ${COLORS_THEME_WHITE.gradientPrimary};
    --gradient-primary-2: ${COLORS_THEME_WHITE.gradientPrimary2};
    --shadow-button: ${COLORS_THEME_WHITE.shadowButton};

    --color-table-header-background: ${COLORS_THEME_WHITE.table.headerBackground};
    --color-table-header-color: ${COLORS_THEME_WHITE.table.headerColor};
    --color-table-header-hover: ${COLORS_THEME_WHITE.table.headerHover};
    --color-table-header-active: ${COLORS_THEME_WHITE.table.headerActive};
    --color-golden: ${COLORS_GLOBAL.golden};

    --color-card-background: ${COLORS_THEME_WHITE.card.background};
    --color-card-background-transparent: ${COLORS_THEME_WHITE.card.backgroundTransparent};
    --color-card-background-deactivated: ${GRADIENTS.deactivated};
    --color-card-border: ${COLORS_THEME_WHITE.card.border};
    --color-card-metric-background: ${COLORS_THEME_WHITE.card.metricBackground};
    --color-card-metric-progress-background: ${COLORS_GLOBAL.gray900};
    --color-card-metric-border: ${COLORS_THEME_WHITE.card.metricBorder};

    --color-nav-background: ${COLORS_THEME_WHITE.navbar.background};
    --color-nav-header-color: ${COLORS_THEME_WHITE.navbar.headerColor};
    --color-nav-icon-background: ${COLORS_THEME_WHITE.navbar.iconBackground};
    --color-nav-item-hover: ${OVERLAYS.white10};
    --color-nav-item-hover-active: ${OVERLAYS.white20};
    --color-nav-item-text: ${OVERLAYS.white90};
    --color-nav-border-focus: ${OVERLAYS.white60};
    --color-nav-divider: ${OVERLAYS.white10};
    --color-nav-active-accent: ${OVERLAYS.white95};
    --color-nav-active-underline: ${COLORS_THEME_WHITE.navbar.activeUnderline};
    --color-nav-bottom-accent: ${COLORS_THEME_WHITE.primary};
    --color-nav-bottom-accent-bg: ${COLORS_THEME_WHITE.payment.dateChipBg};

    --color-payment-date-chip-background: ${COLORS_THEME_WHITE.payment.dateChipBg};
    --color-payment-status-complete-background: ${COLORS_THEME_WHITE.payment.statusCompletedBg};
    --color-payment-status-complete-color: ${COLORS_THEME_WHITE.payment.statusCompletedColor};
    --color-payment-status-pending-background: ${COLORS_THEME_WHITE.payment.statusPendingBg};
    --color-payment-status-pending-color: ${COLORS_THEME_WHITE.payment.statusPendingColor};
    --color-payment-status-failed-background: ${COLORS_THEME_WHITE.payment.statusFailedBg};
    --color-payment-status-failed-color: ${COLORS_THEME_WHITE.payment.statusFailedColor};

    --color-calendar-hover-background: ${COLORS_THEME_WHITE.calendar.hoverBackground};
    --color-calendar-disabled-background: ${COLORS_THEME_WHITE.calendar.disabledBackground};

    --color-tab-background: ${COLORS_THEME_WHITE.tab.background};

    --color-primary: ${COLORS_THEME_WHITE.primary};
    --color-on-primary: ${COLORS_GLOBAL.white};
    --color-primary-hover: ${COLORS_THEME_WHITE.primaryHover};

    --color-secondary: ${COLORS_THEME_WHITE.secondary};
    --color-secondary-hover: ${COLORS_THEME_WHITE.secondaryHover};

    --color-ternary: ${COLORS_THEME_WHITE.ternary};

    --color-button-cancel-color: ${COLORS_THEME_WHITE.button.cancelColor};
    --color-button-cancel-background: ${COLORS_THEME_WHITE.button.cancelBackground};
    --color-button-cancel-border: ${COLORS_THEME_WHITE.button.cancelBorder};
    --color-button-cancel-hover-background: ${COLORS_THEME_WHITE.button.cancelHoverBackground};
    --color-button-cancel-hover-border: ${COLORS_THEME_WHITE.button.cancelHoverBorder};
    --color-button-cancel-active-background: ${COLORS_THEME_WHITE.button.cancelActiveBackground};
    --color-button-cancel-active-border: ${COLORS_THEME_WHITE.button.cancelActiveBorder};
    
    --color-box-shadow: ${COLORS_THEME_WHITE.hover};

    --color-text-default: ${COLORS_GLOBAL.textPrimary};
    --color-text-secondary: ${COLORS_GLOBAL.textSecondary};
    --color-text-muted: ${COLORS_GLOBAL.textMuted};

    --color-negative: ${COLORS_THEME_WHITE.negativeColor};
    --color-negative-border: ${COLORS_THEME_WHITE.negativeColorBorder};

    --color-positive: ${COLORS_THEME_WHITE.positiveColor};
    --color-positive-border: ${COLORS_THEME_WHITE.positiveColorBorder};

    /* Light theme status colors with new soft variants */
    --color-success: ${COLORS_THEME_WHITE.statusSuccess};
    /* The light-theme accent green (#059669) is only 3.77:1 on white; small success text uses the
       darker global success (#15803D, ~5:1) so signed figures meet AA. */
    --color-success-text: ${COLORS_GLOBAL.success};
    --color-success-hover: ${COLORS_THEME_WHITE.statusSuccessHover};
    --color-success-background: ${COLORS_THEME_WHITE.statusSuccessBackground};
    --color-success-border: ${COLORS_THEME_WHITE.statusSuccessBorder};
    --color-status-success: ${COLORS_THEME_WHITE.statusSuccess};
    --color-status-success-background: ${COLORS_THEME_WHITE.statusSuccessBackground};
    --color-status-success-background-soft: ${COLORS_THEME_WHITE.statusSuccessBackgroundSoft};
    --color-status-success-border: ${COLORS_THEME_WHITE.statusSuccessBorder};
    --color-status-success-border-soft: ${COLORS_THEME_WHITE.statusSuccessBorderSoft};

    --color-error: ${COLORS_THEME_WHITE.statusError};
    --color-error-hover: ${COLORS_THEME_WHITE.statusErrorHover};
    --color-error-background: ${COLORS_THEME_WHITE.statusErrorBackground};
    --color-error-border: ${COLORS_THEME_WHITE.statusErrorBorder};
    --color-status-error: ${COLORS_THEME_WHITE.statusError};
    --color-status-error-background: ${COLORS_THEME_WHITE.statusErrorBackground};
    --color-status-error-background-soft: ${COLORS_THEME_WHITE.statusErrorBackgroundSoft};
    --color-status-error-border: ${COLORS_THEME_WHITE.statusErrorBorder};
    --color-status-error-border-soft: ${COLORS_THEME_WHITE.statusErrorBorderSoft};

    --color-warning: ${COLORS_THEME_WHITE.statusWarning};
    --color-warning-hover: ${COLORS_THEME_WHITE.statusWarningHover};
    --color-warning-background: ${COLORS_THEME_WHITE.statusWarningBackground};
    --color-warning-border: ${COLORS_THEME_WHITE.statusWarningBorder};
    --color-status-warning: ${COLORS_THEME_WHITE.statusWarning};
    --color-status-warning-background: ${COLORS_THEME_WHITE.statusWarningBackground};
    --color-status-warning-background-soft: ${COLORS_THEME_WHITE.statusWarningBackgroundSoft};
    --color-status-warning-border: ${COLORS_THEME_WHITE.statusWarningBorder};
    --color-status-warning-border-soft: ${COLORS_THEME_WHITE.statusWarningBorderSoft};

    --color-info: ${COLORS_THEME_WHITE.statusInfo};
    --color-info-background: ${COLORS_THEME_WHITE.statusInfoBackground};
    --color-info-border: ${COLORS_THEME_WHITE.statusInfoBorder};
    --color-status-info: ${COLORS_THEME_WHITE.statusInfo};
    --color-status-info-background: ${COLORS_THEME_WHITE.statusInfoBackground};
    --color-status-info-background-soft: ${COLORS_THEME_WHITE.statusInfoBackgroundSoft};
    --color-status-info-border: ${COLORS_THEME_WHITE.statusInfoBorder};
    --color-status-info-border-soft: ${COLORS_THEME_WHITE.statusInfoBorderSoft};

    --color-select-text: ${COLORS_GLOBAL.black};
    --color-select-option-text: ${COLORS_GLOBAL.black};

    --color-select-icon: ${COLORS_GLOBAL.gray400};

    /* Hero section and detail page colors */
    --hero-background: ${COLORS_THEME_WHITE.heroBackground};
    --hero-footer-background: ${COLORS_THEME_WHITE.heroFooterBackground};
    --detail-icon-background: ${COLORS_THEME_WHITE.detailIconBackground};
    --hover-background: ${COLORS_THEME_WHITE.hoverBackground};
    
    /* Tooltip colors */
    --color-tooltip-bg: ${COLORS_THEME_WHITE.tooltipBackground};
    --color-tooltip-text: ${COLORS_THEME_WHITE.tooltipText};
    --color-tooltip-border: ${COLORS_THEME_WHITE.tooltipBorder};
    
    /* Border colors */
    --color-border-donut: ${COLORS_THEME_WHITE.border};
    --color-border: ${COLORS_THEME_WHITE.border};
    --color-border-light: ${COLORS_THEME_WHITE.borderLight};
    
    /* Tab colors */
    --color-tab-hover-background: ${COLORS_THEME_WHITE.tabHoverBackground};
    
    /* Info box colors */
    --color-info-box-background: ${COLORS_THEME_WHITE.infoBoxBackground};
    --color-info-box-border: ${COLORS_THEME_WHITE.infoBoxBorder};
    --color-info-box-text: ${COLORS_THEME_WHITE.infoBoxText};
    
    /* Toggle button colors */
    --color-toggle-group-background: ${COLORS_THEME_WHITE.toggleGroupBackground};
    --color-toggle-active-background: ${COLORS_THEME_WHITE.toggleActiveBackground};
    --color-toggle-hover-background: ${COLORS_THEME_WHITE.toggleHoverBackground};
    --shadow-toggle-active: ${COLORS_THEME_WHITE.toggleActiveShadow};
    
    /* Skeleton loader gradients for light theme */
    --gradient-skeleton: ${COLORS_THEME_WHITE.skeletonGradient};
    --gradient-skeleton-2: ${COLORS_THEME_WHITE.skeletonGradient2};

    /* Focus shadows for light theme */
    --shadow-focus: ${COLORS_THEME_WHITE.focusShadow};
    
    /* Empty State Colors for light theme */
    --empty-state-background: ${COLORS_THEME_WHITE.emptyStateBackground};
    --empty-state-icon-background: ${COLORS_THEME_WHITE.emptyStateIconBackground};
    --empty-state-icon-color: ${COLORS_THEME_WHITE.emptyStateIconColor};
    --empty-state-border: ${COLORS_THEME_WHITE.emptyStateBorder};
    
    /* Overlay Background for light theme */
    --color-background-overlay: ${COLORS_THEME_WHITE.backgroundOverlay};

    /* Notification Colors - Light mode */
    --notification-success-text: ${COLORS_THEME_WHITE.notificationSuccessText};
    --notification-success-border: ${COLORS_THEME_WHITE.notificationSuccessBorder};
    --notification-success-background-start: ${COLORS_THEME_WHITE.notificationSuccessBackgroundStart};
    --notification-success-background-end: ${COLORS_THEME_WHITE.notificationSuccessBackgroundEnd};

    --notification-info-text: ${COLORS_THEME_WHITE.notificationInfoText};
    --notification-info-border: ${COLORS_THEME_WHITE.notificationInfoBorder};
    --notification-info-background-start: ${COLORS_THEME_WHITE.notificationInfoBackgroundStart};
    --notification-info-background-end: ${COLORS_THEME_WHITE.notificationInfoBackgroundEnd};

    --notification-warning-text: ${COLORS_THEME_WHITE.notificationWarningText};
    --notification-warning-border: ${COLORS_THEME_WHITE.notificationWarningBorder};
    --notification-warning-background-start: ${COLORS_THEME_WHITE.notificationWarningBackgroundStart};
    --notification-warning-background-end: ${COLORS_THEME_WHITE.notificationWarningBackgroundEnd};

    --notification-error-text: ${COLORS_THEME_WHITE.notificationErrorText};
    --notification-error-border: ${COLORS_THEME_WHITE.notificationErrorBorder};
    --notification-error-background-start: ${COLORS_THEME_WHITE.notificationErrorBackgroundStart};
    --notification-error-background-end: ${COLORS_THEME_WHITE.notificationErrorBackgroundEnd};
}

  /* Focus indicators */
  :focus-visible {
    outline: 2px solid var(--color-primary);
    outline-offset: 2px;
    border-radius: 4px;
  }

  [role="button"]:focus-visible,
  button:focus-visible {
    outline: 3px solid var(--color-primary);
    outline-offset: 2px;
    box-shadow: 0 0 0 4px var(--shadow-focus);
  }

  /* Reduced motion */
  @media (prefers-reduced-motion: reduce) {
    *, *::before, *::after {
      animation-duration: 0.01ms !important;
      animation-iteration-count: 1 !important;
      transition-duration: 0.01ms !important;
    }
  }

`;

export default GlobalStyles;
